import { AfterViewInit, Component, DestroyRef, effect, inject, input, OnDestroy, viewChildren } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucidePlus } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';
import { BlocCompétences } from '../../../croisement-référentiel/models/bloc-compétences';
import { Compétence } from '../../../croisement-référentiel/models/compétences';
import { ToastrErrorComponent } from '../../../shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '../../../shared/components/toastr/toastr-success.component';
import { FormComponent } from '../../../shared/models/form-component';
import { BlocCompétencesStore } from '../../store/bloc-compétences.store';
import { CompetenceComponent } from '../competence/competence.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { DialogConfirmDeleteComponent } from '@shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { CompétenceStore } from '../../store/compétences.store';

@Component({
  selector: 'app-gestion',
  imports: [NgIcon, HlmSeparatorImports, CompetenceComponent, HlmButtonImports, RouterModule],
  templateUrl: './gestion.component.html',
  providers: [provideIcons({ lucideCheck, lucidePlus })],
})
export class GestionComponent implements FormComponent, AfterViewInit, OnDestroy {
  readonly #blocCompetencesStore = inject(BlocCompétencesStore);
  readonly #competenceStore = inject(CompétenceStore);
  readonly #fb = inject(FormBuilder);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);

  readonly referentiel = input.required<Référentiel>();
  readonly bloc = input.required<BlocCompétences>();

  #initialCompetences: Compétence[] = [];
  form = this.#fb.group({});

  readonly competenceComponents = viewChildren(CompetenceComponent);

  constructor() {
    effect(() => {
      const bloc = this.bloc();
      if (bloc) {
        this.#initialCompetences = structuredClone(bloc.competences, { transfer: [] });
      }
    });
  }

  ngAfterViewInit() {
    const textareas = this.competenceComponents();

    if (textareas.length) textareas[0].focusTextarea();
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  addCompetence() {
    const bloc = this.#blocCompetencesStore.selectedEntity();
    if (!bloc) return;

    const competence: Compétence = {
      id: uuidv7(),
      libelle: '',
      estValidee: false,
      aptitudes: [],
    };
    this.#blocCompetencesStore.ajouterCompétence(competence);
    this.form.markAllAsDirty();
  }

  editCompetence = (value: Compétence) => {
    this.#blocCompetencesStore.modifierCompétence(value);
    this.form.markAllAsDirty();
    const idx = this.#initialCompetences.findIndex((x) => x.id === value.id);
    this.#initialCompetences[idx] = value;
  };

  deleteCompetence = (competence: Compétence) => {
    const nbrAptitudesLiées = competence.aptitudes.length;

    if (nbrAptitudesLiées > 0) {
      let dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: competence.libelle,
          childrenText: `${competence.aptitudes.length} aptitudes liées`,
          typeElement: 'la compétence',
        },
      });
      const toastrId = uuidv7();

      dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
        if (result) {
          this.#competenceStore.supprimer({
            idCompetence: competence.id,
            next: () => {
              this.#blocCompetencesStore.supprimerCompétence(competence);
              toast.custom(ToastrSuccessComponent, {
                componentProps: { message: 'Suppression de la compétence réussie', id: toastrId },
                id: toastrId,
              });
            },
          });
        }
      });
    } else {
      this.#blocCompetencesStore.supprimerCompétence(competence);
    }
    this.form.markAllAsDirty();
  };

  submit() {
    const bloc = this.#blocCompetencesStore.selectedEntity();
    if (!bloc) return;
    const isValid = bloc.competences
      .filter((x) => x.libelle.trim() !== '')
      .map((x) => x.estValidee)
      .every((x) => !!x);

    const toastrId = uuidv7();

    if (!isValid) {
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          titre: 'Impossible de valider',
          message: 'Veuillez valider ou invalider les compétences héritées du bloc de compétences pour continuer.',
          id: toastrId,
        },
        duration: Number.POSITIVE_INFINITY,
        id: toastrId,
      });
      return;
    }

    this.#blocCompetencesStore.modifier({
      bloc,
      idReferentiel: this.referentiel().id,
      next: () => {
        this.form.reset();
        toast.custom(ToastrSuccessComponent, {
          componentProps: { message: 'Modification du bloc de compétences réussie.', id: toastrId },
          id: toastrId,
        });
      },
    });

    this.form.reset();
    this.#router.navigate(['aptitudes'], { relativeTo: this.#route });
  }

  // Vient de l'implémentation de FormComponent.
  fillForm = () => {};
}
